import React, { useEffect, useState, useCallback, forwardRef } from 'react';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    IconButton,
    Stack,
    Typography,
    Container,
    Divider,
    Avatar,
    Dialog,
    DialogContent,
    Slide
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { Smartphone, Monitor, Tablet, Globe, Clock, Trash2, Shield, ArrowLeft, LogOut } from 'lucide-react';

import { getActiveDevices, revokeDevice, revokeOtherDevices } from 'api/api.auth';

// Transition for Dialog
const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// JWT Decoder using standard browser atob
const decodeJwt = (token) => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        let payload = parts[1];
        payload = payload.replace(/-/g, '+').replace(/_/g, '/');
        while (payload.length % 4) {
            payload += '=';
        }
        const decoded = atob(payload);
        return JSON.parse(decoded);
    } catch (error) {
        console.error('Error decoding JWT token:', error);
        return null;
    }
};

// Relative Time Formatter
const getRelativeTime = (timeString) => {
    try {
        const date = new Date(timeString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);

        if (isNaN(seconds)) return 'Unknown';
        if (seconds < 10) return 'Active now';
        if (seconds < 60) return 'Active just now';

        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `Active ${minutes}m ago`;

        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `Active ${hours}h ago`;

        const days = Math.floor(hours / 24);
        if (days === 1) return 'Active Yesterday';
        return `Active ${days} days ago`;
    } catch (e) {
        return 'Recently';
    }
};

export const Devices = () => {
    const navigate = useNavigate();
    const token = useSelector((state) => state.auth.token);

    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentSessionId, setCurrentSessionId] = useState(null);

    // Dialog state for confirmation
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        actionType: null, // 'revokeOthers' | 'revokeSession'
        targetId: null,
        targetName: '',
        loading: false
    });

    // Redesigned colors based on screenshot
    const appBgColor = '#FCF5E9';
    const orangeColor = '#E27D26';

    // Parse current session ID from JWT
    useEffect(() => {
        if (token) {
            const decoded = decodeJwt(token);
            if (decoded && decoded.dsi) {
                setCurrentSessionId(decoded.dsi);
            }
        }
    }, [token]);

    // Fetch active sessions
    const fetchSessions = useCallback(async (showLoader = true) => {
        if (showLoader) setLoading(true);
        try {
            const response = await getActiveDevices();
            if (response.ok) {
                setSessions(response.data?.data || []);
            } else {
                toast.error(response.data?.message || 'Failed to retrieve active devices.');
            }
        } catch (err) {
            toast.error('An error occurred while fetching devices.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    // Revoke other device sessions (trigger dialog)
    const handleRevokeOthers = () => {
        setConfirmDialog({
            open: true,
            title: 'Log Out Other Devices',
            message:
                'Are you sure you want to log out of all other devices? Any session other than this one will be terminated immediately.',
            actionType: 'revokeOthers',
            targetId: null,
            targetName: '',
            loading: false
        });
    };

    // Revoke a single device session (trigger dialog)
    const handleRevokeSession = (sessionId, deviceName) => {
        setConfirmDialog({
            open: true,
            title: 'Log Out Device',
            message: `Are you sure you want to log out from "${deviceName}"? This will terminate the session immediately.`,
            actionType: 'revokeSession',
            targetId: sessionId,
            targetName: deviceName,
            loading: false
        });
    };

    // Execute the revocation api call
    const handleConfirmAction = async () => {
        const { actionType, targetId } = confirmDialog;
        setConfirmDialog((prev) => ({ ...prev, loading: true }));

        try {
            let response;
            if (actionType === 'revokeOthers') {
                response = await revokeOtherDevices();
            } else if (actionType === 'revokeSession' && targetId) {
                response = await revokeDevice(targetId);
            }

            if (response && response.ok) {
                toast.success(actionType === 'revokeOthers' ? 'Other devices logged out successfully.' : 'Session terminated.');
                setConfirmDialog({
                    open: false,
                    title: '',
                    message: '',
                    actionType: null,
                    targetId: null,
                    targetName: '',
                    loading: false
                });
                fetchSessions();
            } else {
                toast.error(response?.data?.message || 'Failed to terminate session.');
                setConfirmDialog((prev) => ({ ...prev, loading: false }));
            }
        } catch (err) {
            toast.error('An error occurred.');
            setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
    };

    // Format device title helper
    const getDeviceTitle = (session) => {
        if (session.deviceModel) {
            let title = session.deviceModel;
            if (session.deviceVendor && !title.toLowerCase().includes(session.deviceVendor.toLowerCase())) {
                title = `${session.deviceVendor} ${title}`;
            }
            return title;
        }
        if (session.osName) {
            return `${session.osName} ${session.osVersion || ''}`;
        }
        return 'Unknown Device';
    };

    // Format OS / Browser helper
    const getDeviceSubtitle = (session) => {
        const details = [];
        if (session.osName && session.deviceModel) {
            details.push(`${session.osName} ${session.osVersion || ''}`);
        }
        if (session.browserName) {
            details.push(`${session.browserName} ${session.browserVersion || ''}`);
        }
        return details.join(' • ') || 'Session active';
    };

    // Detect Device Icon
    const getDeviceIcon = (deviceType, osName) => {
        const os = (osName || '').toLowerCase();
        const type = (deviceType || '').toLowerCase();

        if (os.includes('ios') || os.includes('android') || type === 'mobile') {
            return <Smartphone size={24} />;
        }
        if (os.includes('mac') || os.includes('windows') || os.includes('linux') || type === 'desktop') {
            return <Monitor size={24} />;
        }
        return <Tablet size={24} />;
    };

    const currentSession = sessions.find((s) => s.sessionId === currentSessionId);
    const otherSessions = sessions.filter((s) => s.sessionId !== currentSessionId);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: appBgColor, pb: 6 }}>
            <Container maxWidth="sm" sx={{ pt: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 3 } }}>
                {/* Header Back Press */}
                <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                    <IconButton
                        onClick={() => navigate(-1)}
                        sx={{
                            bgcolor: 'white',
                            color: '#000000',
                            border: `1px solid ${alpha('#000000', 0.1)}`,
                            '&:hover': { bgcolor: alpha('#000000', 0.05) }
                        }}
                    >
                        <ArrowLeft size={20} />
                    </IconButton>
                    <Typography sx={{ fontSize: '20px', fontWeight: 800, color: '#111827' }}>Active Devices</Typography>
                </Stack>

                {loading ? (
                    <Box sx={{ py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress sx={{ color: orangeColor }} />
                        <Typography sx={{ mt: 2, color: '#6B7280', fontWeight: 600, fontSize: '14px' }}>
                            Loading active sessions...
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={3.5}>
                        {/* THIS DEVICE SECTION */}
                        <Stack spacing={1.5}>
                            <Typography
                                sx={{
                                    fontSize: '11px',
                                    fontWeight: 800,
                                    color: '#9CA3AF',
                                    textTransform: 'uppercase',
                                    letterSpacing: '1px'
                                }}
                            >
                                This Device
                            </Typography>

                            {currentSession ? (
                                <Card
                                    elevation={0}
                                    sx={{
                                        p: { xs: 1.5, sm: 2 },
                                        borderRadius: '16px',
                                        border: `1.5px solid ${alpha('#000', 0.05)}`,
                                        bgcolor: '#FFFFFF'
                                    }}
                                >
                                    <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
                                        <Avatar
                                            sx={{
                                                bgcolor: alpha(orangeColor, 0.1),
                                                color: orangeColor,
                                                borderRadius: '12px',
                                                width: { xs: 40, sm: 44 },
                                                height: { xs: 40, sm: 44 }
                                            }}
                                        >
                                            {getDeviceIcon(currentSession.deviceType, currentSession.osName)}
                                        </Avatar>
                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography noWrap sx={{ fontSize: '15px', fontWeight: 800, color: '#1F2937' }}>
                                                    {getDeviceTitle(currentSession)}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        bgcolor: '#DEF7EC',
                                                        color: '#03543F',
                                                        borderRadius: '24px',
                                                        px: 1.2,
                                                        py: 0.25,
                                                        fontSize: '10px',
                                                        fontWeight: 700
                                                    }}
                                                >
                                                    Current
                                                </Box>
                                            </Stack>
                                            <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.25 }}>
                                                {getDeviceSubtitle(currentSession)}
                                            </Typography>
                                            <Stack direction="row" spacing={0.8} alignItems="center" mt={0.8}>
                                                <Globe size={11} color="#9CA3AF" />
                                                <Typography
                                                    sx={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 600, wordBreak: 'break-all' }}
                                                >
                                                    {currentSession.ipAddress || 'Unknown IP'}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Card>
                            ) : (
                                <Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Could not identify this session</Typography>
                            )}
                        </Stack>

                        {/* OTHER ACTIVE DEVICES SECTION */}
                        <Stack spacing={1.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography
                                    sx={{
                                        fontSize: '11px',
                                        fontWeight: 800,
                                        color: '#9CA3AF',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px'
                                    }}
                                >
                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                        Other Active Devices
                                    </Box>
                                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                        Other Devices
                                    </Box>{' '}
                                    ({otherSessions.length})
                                </Typography>
                                {otherSessions.length > 0 && (
                                    <Button
                                        onClick={handleRevokeOthers}
                                        sx={{
                                            color: '#EF4444',
                                            fontWeight: 800,
                                            fontSize: { xs: '11px', sm: '12px' },
                                            textTransform: 'none',
                                            p: 0,
                                            minWidth: 0,
                                            whiteSpace: 'nowrap',
                                            '&:hover': { bgcolor: 'transparent', color: '#DC2626' }
                                        }}
                                    >
                                        Log Out Others
                                    </Button>
                                )}
                            </Stack>

                            {otherSessions.length > 0 ? (
                                <Stack spacing={2}>
                                    {otherSessions.map((session) => {
                                        const title = getDeviceTitle(session);
                                        return (
                                            <Card
                                                key={session.sessionId}
                                                elevation={0}
                                                sx={{
                                                    p: { xs: 1.5, sm: 2 },
                                                    borderRadius: '16px',
                                                    border: `1.5px solid ${alpha('#000', 0.05)}`,
                                                    bgcolor: '#FFFFFF'
                                                }}
                                            >
                                                <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: alpha('#4B5563', 0.1),
                                                            color: '#4B5563',
                                                            borderRadius: '12px',
                                                            width: { xs: 40, sm: 44 },
                                                            height: { xs: 40, sm: 44 }
                                                        }}
                                                    >
                                                        {getDeviceIcon(session.deviceType, session.osName)}
                                                    </Avatar>
                                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                        <Typography noWrap sx={{ fontSize: '15px', fontWeight: 800, color: '#1F2937' }}>
                                                            {title}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.25 }}>
                                                            {getDeviceSubtitle(session)}
                                                        </Typography>
                                                        <Stack
                                                            direction={{ xs: 'column', sm: 'row' }}
                                                            spacing={{ xs: 0.5, sm: 1.5 }}
                                                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                                                            mt={0.8}
                                                        >
                                                            <Stack direction="row" spacing={0.8} alignItems="center">
                                                                <Globe size={11} color="#9CA3AF" />
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '10px',
                                                                        color: '#9CA3AF',
                                                                        fontWeight: 600,
                                                                        wordBreak: 'break-all'
                                                                    }}
                                                                >
                                                                    {session.ipAddress || 'Unknown IP'}
                                                                </Typography>
                                                            </Stack>
                                                            <Typography
                                                                sx={{
                                                                    display: { xs: 'none', sm: 'block' },
                                                                    fontSize: '10px',
                                                                    color: '#E5E7EB'
                                                                }}
                                                            >
                                                                |
                                                            </Typography>
                                                            <Stack direction="row" spacing={0.8} alignItems="center">
                                                                <Clock size={11} color="#9CA3AF" />
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '10px',
                                                                        color: '#9CA3AF',
                                                                        fontWeight: 600,
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    {getRelativeTime(session.lastActiveAt)}
                                                                </Typography>
                                                            </Stack>
                                                        </Stack>
                                                    </Box>
                                                    <IconButton
                                                        onClick={() => handleRevokeSession(session.sessionId, title)}
                                                        sx={{
                                                            color: '#EF4444',
                                                            p: { xs: 0.5, sm: 1 },
                                                            '&:hover': { bgcolor: alpha('#EF4444', 0.08) }
                                                        }}
                                                    >
                                                        <Trash2 size={18} />
                                                    </IconButton>
                                                </Stack>
                                            </Card>
                                        );
                                    })}
                                </Stack>
                            ) : (
                                <Card
                                    elevation={0}
                                    sx={{
                                        p: 4,
                                        borderRadius: '24px',
                                        border: `1.5px solid ${alpha('#000', 0.05)}`,
                                        bgcolor: '#FFFFFF',
                                        textAlign: 'center',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1.5
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: alpha('#9CA3AF', 0.1),
                                            color: '#9CA3AF',
                                            width: 56,
                                            height: 56
                                        }}
                                    >
                                        <Shield size={28} />
                                    </Avatar>
                                    <Typography sx={{ fontSize: '15px', fontWeight: 800, color: '#374151' }}>
                                        No other active devices
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#6B7280', maxWidth: 280, mx: 'auto' }}>
                                        You are currently logged in on this device only.
                                    </Typography>
                                </Card>
                            )}
                        </Stack>
                    </Stack>
                )}
            </Container>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialog.open}
                onClose={() => !confirmDialog.loading && setConfirmDialog((prev) => ({ ...prev, open: false }))}
                TransitionComponent={Transition}
                keepMounted
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        p: 3,
                        overflow: 'hidden',
                        bgcolor: '#FFFFFF',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }
                }}
            >
                <DialogContent sx={{ p: 0 }}>
                    <Avatar
                        sx={{
                            bgcolor: alpha('#EF4444', 0.1),
                            color: '#EF4444',
                            width: 56,
                            height: 56,
                            mx: 'auto',
                            mb: 2.5
                        }}
                    >
                        <LogOut size={26} />
                    </Avatar>
                    <Typography
                        sx={{
                            textAlign: 'center',
                            fontSize: '18px',
                            fontWeight: 800,
                            color: '#111827',
                            mb: 1.5
                        }}
                    >
                        {confirmDialog.title}
                    </Typography>
                    <Typography
                        sx={{
                            textAlign: 'center',
                            fontSize: '14px',
                            color: '#4B5563',
                            mb: 4,
                            lineHeight: 1.5,
                            px: 1
                        }}
                    >
                        {confirmDialog.message}
                    </Typography>
                    <Stack direction="row" spacing={2} width="100%">
                        <Button
                            fullWidth
                            variant="outlined"
                            disabled={confirmDialog.loading}
                            onClick={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
                            sx={{
                                py: 1.5,
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 700,
                                borderColor: '#E5E7EB',
                                color: '#475569',
                                '&:hover': {
                                    borderColor: '#D1D5DB',
                                    bgcolor: '#F9FAFB'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={confirmDialog.loading}
                            onClick={handleConfirmAction}
                            sx={{
                                py: 1.5,
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 700,
                                bgcolor: '#EF4444',
                                color: '#FFFFFF',
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: '#DC2626',
                                    boxShadow: 'none'
                                },
                                '&:disabled': {
                                    bgcolor: alpha('#EF4444', 0.5),
                                    color: '#FFFFFF'
                                }
                            }}
                        >
                            {confirmDialog.loading ? <CircularProgress size={20} color="inherit" /> : 'Log Out'}
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default Devices;
